/**
 * EquipmentComponent - Manages equipped armor and accessories
 */

import { ArmorItem, EquipmentSlot, EquipmentBonuses } from './Item';

export interface EquippedItems {
  head: ArmorItem | null;
  chest: ArmorItem | null;
  legs: ArmorItem | null;
  feet: ArmorItem | null;
  hands: ArmorItem | null;
  accessory: ArmorItem | null;
}

export class EquipmentComponent {
  private equipped: EquippedItems = {
    head: null,
    chest: null,
    legs: null,
    feet: null,
    hands: null,
    accessory: null
  };

  /** Equip an armor item to its slot */
  equip(item: ArmorItem): ArmorItem | null {
    const slot = item.slot;
    const previousItem = this.equipped[slot];
    this.equipped[slot] = item;
    return previousItem; // Return the item that was unequipped (if any)
  }

  /** Unequip item from a slot */
  unequip(slot: EquipmentSlot): ArmorItem | null {
    const item = this.equipped[slot];
    this.equipped[slot] = null;
    return item;
  }

  /** Get item in a slot */
  getSlot(slot: EquipmentSlot): ArmorItem | null {
    return this.equipped[slot];
  }

  /** Get all equipped items */
  getAllEquipped(): EquippedItems {
    return { ...this.equipped };
  }

  /** Check if a slot has an item */
  hasItemInSlot(slot: EquipmentSlot): boolean {
    return this.equipped[slot] !== null;
  }

  /** Calculate total defense from all equipped items */
  getTotalDefense(): number {
    let defense = 0;
    for (const slot of Object.keys(this.equipped) as EquipmentSlot[]) {
      const item = this.equipped[slot];
      if (item) {
        defense += item.getTotalDefense();
      }
    }
    return defense;
  }

  /** Calculate combined bonuses from all equipped items */
  getTotalBonuses(): EquipmentBonuses {
    const bonuses: EquipmentBonuses = {
      defense: 0,
      maxHealth: 0,
      maxStamina: 0,
      moveSpeed: 0,
      attackDamage: 0,
      attackSpeed: 0,
      gatherSpeed: 0
    };

    for (const slot of Object.keys(this.equipped) as EquipmentSlot[]) {
      const item = this.equipped[slot];
      if (item && item.bonuses) {
        bonuses.defense! += item.bonuses.defense ?? 0;
        bonuses.maxHealth! += item.bonuses.maxHealth ?? 0;
        bonuses.maxStamina! += item.bonuses.maxStamina ?? 0;
        bonuses.moveSpeed! += item.bonuses.moveSpeed ?? 0;
        bonuses.attackDamage! += item.bonuses.attackDamage ?? 0;
        bonuses.attackSpeed! += item.bonuses.attackSpeed ?? 0;
        bonuses.gatherSpeed! += item.bonuses.gatherSpeed ?? 0;
      }
    }

    // Add base defense from armor
    bonuses.defense! += this.getTotalDefense();

    return bonuses;
  }

  /** Apply damage to equipped armor (reduces durability) */
  applyDamageToArmor(damage: number): void {
    for (const slot of Object.keys(this.equipped) as EquipmentSlot[]) {
      const item = this.equipped[slot];
      if (item) {
        item.takeDamage(damage);
      }
    }
  }

  /** Get save data */
  toSaveData(): { slot: EquipmentSlot; itemId: string }[] {
    const data: { slot: EquipmentSlot; itemId: string }[] = [];
    for (const slot of Object.keys(this.equipped) as EquipmentSlot[]) {
      const item = this.equipped[slot];
      if (item) {
        data.push({ slot, itemId: item.id });
      }
    }
    return data;
  }

  /** Load from save data */
  fromSaveData(data: { slot: EquipmentSlot; itemId: string }[], itemRegistry: { get: (id: string) => ArmorItem | null }): void {
    // Clear current equipment
    this.equipped = {
      head: null,
      chest: null,
      legs: null,
      feet: null,
      hands: null,
      accessory: null
    };

    // Load saved equipment
    for (const entry of data) {
      const item = itemRegistry.get(entry.itemId);
      if (item instanceof ArmorItem) {
        this.equipped[entry.slot] = item;
      }
    }
  }
}
